import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import {
  buildModuleSearchUrl,
  readSearchQueryFromParams,
  resolveSearchModule,
} from '../config/globalHeaderSearch';
import { fetchGlobalSearchSuggestions } from '../services/globalHeaderSearchSuggest.service';
import { getRecentSearches, pushRecentSearch } from '../utils/globalHeaderSearchRecent';
import { splitQueryHighlight } from '../utils/globalHeaderSearchQueryCompletions';

const DEBOUNCE_MS = 350;
const SUGGESTIONS_LIST_ID = 'global-header-search-suggestions';
const MIN_SUGGEST_LENGTH = 1;

const VARIANT_CLASSES = {
  hub: {
    form: 'hub-home-header__search',
    wrap: 'hub-home-header__search-wrap global-header-search',
    input: 'hub-home-header__search-input',
    button: 'hub-home-header__search-btn',
  },
  common: {
    form: 'common-header-search',
    wrap: 'common-header-search-wrapper global-header-search',
    input: 'common-header-search-input',
    button: 'common-header-search-button',
  },
};

function QuerySuggestionLabel({ label, query }) {
  const { typed, completion } = splitQueryHighlight(label, query);
  return (
    <span className="global-header-search__option-label">
      {typed ? <span className="global-header-search__query-muted">{typed}</span> : null}
      {completion ? <span className="global-header-search__query-bold">{completion}</span> : null}
      {!typed && !completion ? label : null}
    </span>
  );
}

/**
 * @param {{ variant?: 'hub' | 'common' }} props
 */
export default function GlobalHeaderSearch({ variant = 'hub' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const classes = VARIANT_CLASSES[variant] || VARIANT_CLASSES.hub;

  const module = useMemo(() => resolveSearchModule(location.pathname), [location.pathname]);
  const onActiveModule = useMemo(
    () => module.pathPatterns.some((pattern) => pattern.test(location.pathname)),
    [module, location.pathname],
  );
  const urlQuery = readSearchQueryFromParams(searchParams);

  const [query, setQuery] = useState(() => (onActiveModule ? urlQuery : ''));
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentVersion, setRecentVersion] = useState(0);

  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const recentSearches = useMemo(
    () => getRecentSearches(module.id),
    [module.id, recentVersion],
  );

  useEffect(() => {
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
    setQuery(onActiveModule ? urlQuery : '');
  }, [module.id, onActiveModule, urlQuery]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (!module.suggestionsEnabled || trimmed.length < MIN_SUGGEST_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const items = await fetchGlobalSearchSuggestions(module.id, trimmed, controller.signal);
        if (controller.signal.aborted) return;
        setSuggestions(items);
        setOpen(true);
        setActiveIndex(-1);
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, module.id, module.suggestionsEnabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const dropdownItems = useMemo(() => {
    const trimmed = query.trim();
    if (trimmed.length >= MIN_SUGGEST_LENGTH && suggestions.length) {
      return suggestions.map((item) => ({ type: 'query', ...item }));
    }
    if (trimmed.length < MIN_SUGGEST_LENGTH && recentSearches.length) {
      return recentSearches.map((label) => ({ type: 'recent', id: label, label }));
    }
    return [];
  }, [query, suggestions, recentSearches]);

  const submitSearch = useCallback(
    (value) => {
      const trimmed = String(value ?? query).trim();
      if (trimmed.length >= MIN_SUGGEST_LENGTH) {
        pushRecentSearch(module.id, trimmed);
        setRecentVersion((v) => v + 1);
      }
      const url = buildModuleSearchUrl(module, trimmed, searchParams, location.pathname);
      setOpen(false);
      setActiveIndex(-1);
      navigate(url);
    },
    [module, query, searchParams, location.pathname, navigate],
  );

  const selectDropdownItem = useCallback(
    (item) => {
      submitSearch(item?.label || query);
    },
    [query, submitSearch],
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    const hasItems = dropdownItems.length > 0;
    const maxIndex = dropdownItems.length - 1;

    if (!open || !hasItems) {
      if (event.key === 'ArrowDown' && (module.suggestionsEnabled || recentSearches.length)) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, maxIndex));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, -1));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0 && activeIndex <= maxIndex) {
      event.preventDefault();
      selectDropdownItem(dropdownItems[activeIndex]);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitSearch();
  };

  const trimmedQuery = query.trim();
  const showRecent = open && trimmedQuery.length < MIN_SUGGEST_LENGTH && recentSearches.length > 0;
  const showSuggestions =
    open && trimmedQuery.length >= MIN_SUGGEST_LENGTH && (suggestions.length > 0 || loading);
  const showDropdown = showRecent || showSuggestions;

  return (
    <form className={classes.form} role="search" onSubmit={handleSubmit}>
      <div className={classes.wrap} ref={wrapRef}>
        <input
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            const next = event.target.value.trim();
            if (next.length >= MIN_SUGGEST_LENGTH && module.suggestionsEnabled) {
              setOpen(true);
            } else if (next.length < MIN_SUGGEST_LENGTH && recentSearches.length) {
              setOpen(true);
            }
          }}
          onFocus={() => {
            if (suggestions.length > 0 || recentSearches.length > 0 || query.trim()) {
              setOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={module.placeholder}
          className={classes.input}
          aria-label={module.placeholder}
          aria-autocomplete="list"
          aria-controls={showDropdown ? SUGGESTIONS_LIST_ID : undefined}
          aria-expanded={showDropdown}
          autoComplete="off"
        />

        {loading ? (
          <span className="global-header-search__loading" aria-hidden="true">
            <FaSpinner className="global-header-search__spinner" />
          </span>
        ) : null}

        <button type="submit" className={classes.button} aria-label="Search">
          <FaSearch aria-hidden />
        </button>

        {showDropdown ? (
          <ul id={SUGGESTIONS_LIST_ID} className="global-header-search__dropdown" role="listbox">
            {showRecent ? (
              <>
                <li className="global-header-search__section-label" role="presentation">
                  Recent searches
                </li>
                {recentSearches.map((label, index) => (
                  <li key={`recent-${label}`} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      className={`global-header-search__option${index === activeIndex ? ' is-active' : ''}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectDropdownItem({ type: 'recent', label })}
                    >
                      <span className="global-header-search__option-label">{label}</span>
                    </button>
                  </li>
                ))}
              </>
            ) : null}

            {showSuggestions && !loading
              ? suggestions.map((item, index) => (
                  <li key={`${item.id}-${index}`} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      className={`global-header-search__option${index === activeIndex ? ' is-active' : ''}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectDropdownItem({ type: 'query', ...item })}
                    >
                      <QuerySuggestionLabel label={item.label} query={trimmedQuery} />
                    </button>
                  </li>
                ))
              : null}

            {showSuggestions && loading ? (
              <li className="global-header-search__section-label" role="presentation">
                Finding suggestions...
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </form>
  );
}
